// src/utils/pdfGenerator.js
import { jsPDF } from 'jspdf';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
// Import the currency utils
import { formatCurrency as formatCurrencyUtil } from './currencyUtils';

/**
 * Generate an invoice PDF
 * @param {Object} transaction - Invoice transaction data
 * @param {Object} business - Business information
 * @param {Object} counterparty - Customer information
 * @param {Function} formatCurrencyFn - Optional currency formatter function
 * @returns {jsPDF} PDF document
 */
export default async function generateInvoicePDF(transaction, business, counterparty, formatCurrencyFn) {
  try {
    // Use the provided formatter or fall back to the imported one
    const currencyFormatter = formatCurrencyFn || formatCurrencyUtil;
    
    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set font sizes
    const titleFontSize = 20;
    const headerFontSize = 12;
    const normalFontSize = 10;
    const smallFontSize = 8;
    
    // Colors - using hex values directly
    doc.setDrawColor(200, 200, 200); // Light gray for borders
    
    // Margins
    const marginLeft = 15;
    const marginTop = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginRight = 15;

    console.log('business data', business);    // Add business logo/header

    const logoUrl = business?.businessLogoUrl || business?.logoUrl;
    if (logoUrl && typeof logoUrl === 'string') {
      try {
        const logoImage = await resolveImageForPdf(logoUrl);
        if (logoImage) {
          const logoSize = getAutoWidthImageSize(doc, logoImage.dataUrl, 20);
          doc.addImage(
            logoImage.dataUrl,
            logoImage.format,
            marginLeft,
            marginTop,
            logoSize.width,
            logoSize.height
          );
        }
      } catch (error) {
        // A missing or unsupported logo should not prevent the invoice export.
        console.warn('Unable to render logo in PDF:', error);
      }
    }

    doc.setFontSize(titleFontSize);
    doc.setTextColor(25, 1, 146); // #190192
    const invoiceTitleY = marginTop + 6;
    const invoiceDetailsStartY = invoiceTitleY + 8;
    doc.text('INVOICE', pageWidth - marginRight, invoiceTitleY, { align: 'right' });
    
    // Add invoice information
    doc.setFontSize(headerFontSize);
    doc.setTextColor(0, 0, 0);
    doc.text(`Invoice #: ${transaction.documentNumber || ''}`, pageWidth - marginRight, invoiceDetailsStartY, { align: 'right' });
    doc.text(`Date: ${formatDate(transaction.issueDate)}`, pageWidth - marginRight, invoiceDetailsStartY + 5, { align: 'right' });
    doc.text(`Due Date: ${formatDate(transaction.dueDate)}`, pageWidth - marginRight, invoiceDetailsStartY + 10, { align: 'right' });
    
    // Add business information
    doc.setFontSize(headerFontSize);
    doc.text('From:', marginLeft, marginTop + 30);
    doc.setFontSize(normalFontSize);
    doc.text(business?.name || 'Your Business', marginLeft, marginTop + 35);
    if (business?.address) {
      doc.text(business.address, marginLeft, marginTop + 40);
    }
    if (business?.email) {
      doc.text(`Email: ${business.email}`, marginLeft, marginTop + 45);
    }
    if (business?.phone) {
      doc.text(`Phone: ${business.phone}`, marginLeft, marginTop + 50);
    }
    
    // Add customer information
    doc.setFontSize(headerFontSize);
    doc.text('Bill To:', 110, marginTop + 30);
    doc.setFontSize(normalFontSize);
    doc.text(getCounterpartyName(counterparty), 110, marginTop + 35);
    if (counterparty?.address) {
      doc.text(counterparty.address, 110, marginTop + 40);
    }
    if (counterparty?.email) {
      doc.text(`Email: ${counterparty.email}`, 110, marginTop + 45);
    }
    if (counterparty?.phone) {
      doc.text(`Phone: ${counterparty.phone}`, 110, marginTop + 50);
    }
    
    // Manually draw table - no dependency on autoTable
    const startY = marginTop + 60;
    const colWidth = [72, 22, 28, 24, 34]; // Fit between the 15 mm page margins
    const colStart = [marginLeft];
    const tableWidth = colWidth.reduce((sum, width) => sum + width, 0);
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableBottom = pageHeight - 25; // Leave space for the footer
    const cellPadding = 3;
    const firstLineOffset = 6;
    const lineHeight = 4.5;
    
    // Calculate column starting positions
    for (let i = 1; i < colWidth.length; i++) {
      colStart[i] = colStart[i-1] + colWidth[i-1];
    }
    
    // Draw table headers
    const rowHeight = 10;
    
    const drawTableHeader = (y) => {
      doc.setFillColor(25, 1, 146); // #190192
      doc.rect(marginLeft, y, tableWidth, rowHeight, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      ['Description', 'Quantity', 'Price', 'Tax', 'Amount'].forEach((label, index) => {
        doc.text(label, colStart[index] + cellPadding, y + 7);
      });
      doc.setTextColor(0, 0, 0);
    };
    drawTableHeader(startY);
    
    // Draw table rows
    let currentY = startY + rowHeight;
    doc.setTextColor(0, 0, 0);
    
    // Draw data rows
    (transaction.items || []).forEach((item, index) => {
      // Format currency values using the provided formatter
      const formattedPrice = currencyFormatter(Number(item.price));
      const formattedTax = currencyFormatter(Number(item.taxAmount));
      const formattedAmount = currencyFormatter(Number(item.amount));
      const values = [item.description, item.quantity, formattedPrice, formattedTax, formattedAmount];
      const cellLines = values.map((value, column) =>
        doc.splitTextToSize(String(value ?? ''), colWidth[column] - 2 * cellPadding)
      );
      const lineCount = Math.max(1, ...cellLines.map((lines) => lines.length));
      let firstLine = 0;

      // A single item may span pages when its description is particularly long.
      while (firstLine < lineCount) {
        if (tableBottom - currentY < rowHeight) {
          doc.addPage();
          drawTableHeader(marginTop);
          currentY = marginTop + rowHeight;
        }

        const availableHeight = tableBottom - currentY;
        const linesThatFit = Math.max(1, Math.floor((availableHeight - 4.5) / lineHeight));
        const linesInPart = Math.min(lineCount - firstLine, linesThatFit);
        const partHeight = Math.max(rowHeight, 9 + (linesInPart - 1) * lineHeight);

        if (index % 2 === 1) {
          doc.setFillColor(240, 240, 240);
          doc.rect(marginLeft, currentY, tableWidth, partHeight, 'F');
        }
        doc.line(marginLeft, currentY, marginLeft + tableWidth, currentY);
        for (let column = 0; column < cellLines.length; column++) {
          for (let lineIndex = 0; lineIndex < linesInPart; lineIndex++) {
            const line = cellLines[column][firstLine + lineIndex];
            if (line !== undefined) {
              doc.text(line, colStart[column] + cellPadding, currentY + firstLineOffset + lineIndex * lineHeight);
            }
          }
        }
        for (const x of [...colStart, marginLeft + tableWidth]) {
          doc.line(x, currentY, x, currentY + partHeight);
        }
        currentY += partHeight;
        firstLine += linesInPart;

        if (firstLine < lineCount) {
          doc.line(marginLeft, currentY, marginLeft + tableWidth, currentY);
          doc.addPage();
          drawTableHeader(marginTop);
          currentY = marginTop + rowHeight;
        }
      }
    });
    
    // Last row line
    doc.line(marginLeft, currentY, marginLeft + tableWidth, currentY);
    
    // Calculate position for totals
    let finalY = currentY + 10;
    if (finalY + (transaction.paidAmount > 0 ? 24 : 12) > tableBottom) {
      doc.addPage();
      finalY = marginTop + 10;
    }
    const totalsLabelX = 130; // X position for labels
    const totalsValueX = colStart[4] + colWidth[4] - 3; // Align with the Amount column

    // Add totals using the currency formatter
    const formattedSubtotal = currencyFormatter(Number(transaction.subtotal));
    const formattedTax = currencyFormatter(Number(transaction.taxAmount));
    const formattedTotal = currencyFormatter(Number(transaction.totalAmount));

    doc.setFontSize(normalFontSize);
    doc.text('Subtotal:', totalsLabelX, finalY);
    doc.text(formattedSubtotal, totalsValueX, finalY, { align: 'right' });

    doc.text('Tax:', totalsLabelX, finalY + 5);
    doc.text(formattedTax, totalsValueX, finalY + 5, { align: 'right' });

    doc.setFontSize(headerFontSize);
    doc.text('Total:', totalsLabelX, finalY + 12);
    doc.text(formattedTotal, totalsValueX, finalY + 12, { align: 'right' });

    // If there's payment information - use the same alignment
    if (transaction.paidAmount > 0) {
      const formattedPaid = currencyFormatter(Number(transaction.paidAmount));
      const formattedBalance = currencyFormatter(Number(transaction.balanceDue));
      
      doc.setFontSize(normalFontSize);
      doc.text('Amount Paid:', totalsLabelX, finalY + 17);
      doc.text(formattedPaid, totalsValueX, finalY + 17, { align: 'right' });
      
      doc.setFontSize(headerFontSize);
      doc.text('Balance Due:', totalsLabelX, finalY + 24);
      doc.text(formattedBalance, totalsValueX, finalY + 24, { align: 'right' });
    }

    // Banking details below totals, aligned to start
    const bankingDetails = business?.bankingDetails || {};
    const bankingEntries = [
      ['Bank Name', bankingDetails.bankName],
      ['Account Name', bankingDetails.accountName],
      ['Account Number', bankingDetails.accountNumber],
      ['Routing Number', bankingDetails.routingNumber],
      ['SWIFT Code', bankingDetails.swiftCode],
      ['IBAN', bankingDetails.iban],
      ['Branch', bankingDetails.branch]
    ].filter(([, value]) => value);

    let postTotalsY = transaction.paidAmount > 0 ? finalY + 30 : finalY + 18;
    if (business?.includeBankingInInvoice && bankingEntries.length > 0) {
      doc.setFontSize(headerFontSize);
      doc.text('Banking Details:', marginLeft, postTotalsY);
      doc.setFontSize(normalFontSize);
      let detailsY = postTotalsY + 5;
      bankingEntries.forEach(([label, value]) => {
        doc.text(`${label}: ${value}`, marginLeft, detailsY);
        detailsY += 5;
      });
      postTotalsY = detailsY + 2;
    }
    
    // Add notes
    if (transaction.notes) {
      doc.setFontSize(headerFontSize);
      doc.text('Notes:', marginLeft, postTotalsY);
      doc.setFontSize(normalFontSize);
      
      // Handle wrapping for long notes
      const splitNotes = doc.splitTextToSize(transaction.notes, 170);
      doc.text(splitNotes, marginLeft, postTotalsY + 5);
    }
    
    // Add footer
    doc.setFontSize(smallFontSize);
    doc.setTextColor(128, 128, 128); // Gray
    doc.text('Thank you for your business!', marginLeft, 280);
    
    return doc;
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}

// Helper functions
let fetchLogoDataUrlCallable = null;

function getFetchLogoDataUrlCallable() {
  if (!fetchLogoDataUrlCallable) {
    fetchLogoDataUrlCallable = httpsCallable(functions, 'fetchLogoDataUrl');
  }
  return fetchLogoDataUrlCallable;
}

async function resolveImageForPdf(url) {
  if (!url || typeof url !== 'string') return null;

  if (url.startsWith('data:image')) {
    const format = getImageFormatFromDataUrl(url);
    if (format) return { dataUrl: url, format };

    // jsPDF does not reliably support SVG data URLs, so convert them to PNG.
    return rasterizeImageToPng(url);
  }

  // Firebase Storage is configured for browser GET requests. Loading through a
  // canvas also normalizes formats such as SVG to a PNG that jsPDF can embed.
  try {
    return await rasterizeImageToPng(url);
  } catch (error) {
    console.warn('Failed to load logo image in the browser:', error);
  }

  try {
    const fetchLogoDataUrl = getFetchLogoDataUrlCallable();
    const result = await fetchLogoDataUrl({ url });
    const dataUrl = result?.data?.dataUrl;
    const format = result?.data?.format;
    if (!dataUrl || !format) return null;
    return { dataUrl, format };
  } catch (error) {
    console.warn('Failed to fetch logo image via function:', error);
    return null;
  }
}

function rasterizeImageToPng(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;

        if (!canvas.width || !canvas.height) {
          reject(new Error('Logo has invalid dimensions.'));
          return;
        }

        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), format: 'PNG' });
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error('Logo image could not be loaded.'));
    image.src = url;
  });
}

function getAutoWidthImageSize(doc, dataUrl, height) {
  const properties = doc.getImageProperties(dataUrl);

  return {
    width: height * (properties.width / properties.height),
    height
  };
}

function getImageFormatFromDataUrl(dataUrl) {
  const match = /^data:(image\/[a-zA-Z0-9.+-]+);/i.exec(dataUrl);
  if (!match) return null;
  return getImageFormatFromMimeType(match[1]);
}

function getImageFormatFromMimeType(mimeType) {
  const normalized = mimeType.toLowerCase();
  if (normalized === 'image/png') return 'PNG';
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return 'JPEG';
  return null;
}

function formatDate(date) {
  if (!date) return '';
  
  if (typeof date === 'object' && date.toDate) {
    date = date.toDate();
  } else if (!(date instanceof Date)) {
    date = new Date(date);
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getCounterpartyName(counterparty) {
  if (!counterparty) return 'Customer';
  
  return counterparty.name || 
         counterparty.businessName || 
         `${counterparty.firstName || ''} ${counterparty.lastName || ''}`.trim() || 
         'Customer';
}
